import { useEffect, useState } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { fetchOnboardingProfile, updateName, type ProfileResult } from '../api/onboarding';
import { useFlagsSettled, useNameGateEnabled } from '../lib/featureFlags';
import { isNameHandled, markNameHandled } from '../lib/nameHandled';
import { logEvent } from '../lib/telemetry';
import { INVALID_NAME_MESSAGE, validateName } from '../lib/validateName';

type CheckState = 'checking' | 'prompt' | 'open';

// The gate fails open; each time it does, say why — the status class only, never the response.
function reportFailOpen(result: Exclude<ProfileResult, { kind: 'ok' }> | null): void {
  let statusClass = 'no_token';
  if (result) statusClass = result.kind === 'failed' ? result.statusClass : '4xx';
  logEvent('name_gate.profile_unavailable', 'Onboarding profile unavailable; the name gate let the user through',
    { status_class: statusClass }, 'WARN');
}

// 'waiting': the profile check is still pending, so nothing behind the gate may render or navigate
// yet. 'prompt': the user has no name on file; show NamePrompt. 'open': go on.
export type NameGateStatus = 'waiting' | 'prompt' | 'open';

export type NameForm = {
  firstName: string;
  lastName: string;
  setFirstName: (value: string) => void;
  setLastName: (value: string) => void;
  firstNameError: string | undefined;
  lastNameError: string | undefined;
  canSubmit: boolean;
  saving: boolean;
  error: string | null;
  submit: () => Promise<void>;
};

// The one decision behind NameGate (the hub routes) and the post-sign-in product bypass, which
// leaves for the product before any gated route renders. `enabled` false reads as open at once.
//
// Never held on LaunchDarkly: while `release-hub-onboarding-name-gate` is unknown or off the status
// is 'open' immediately, exactly as before the gate existed. Only a flag already reading on, for a
// user not yet handled, runs the profile check. With `stayOpen` (NameGate, which renders the hub
// when open) a mount that has been open stays open, so a flag that turns on later never pulls the
// rendered hub away; the next mount checks instead.
export function useNameGate(
  { enabled = true, stayOpen = true }: { enabled?: boolean; stayOpen?: boolean } = {},
): { status: NameGateStatus; form: NameForm } {
  const auth = useAuth();
  const userId = auth.user?.id ?? null;
  const flagsSettled = useFlagsSettled();
  const gateEnabled = useNameGateEnabled();
  const [shownOpen, setShownOpen] = useState(false);
  const unhandled = enabled && userId !== null && !isNameHandled(userId);
  const active = unhandled && flagsSettled && gateEnabled && !shownOpen;
  const [state, setState] = useState<CheckState>('checking');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!active || state !== 'checking' || !userId) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const token = await auth.getAccessToken();
        const result = token ? await fetchOnboardingProfile(token, controller.signal) : null;
        if (controller.signal.aborted) return;
        if (result?.kind === 'ok') {
          if (result.profile.needsName) { setState('prompt'); return; }
          markNameHandled(userId);
        } else {
          reportFailOpen(result);
        }
        setState('open');
      } catch {
        if (controller.signal.aborted) return;
        reportFailOpen(null);
        setState('open');
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one check per user; auth identity churns
  }, [active, state, userId]);

  const firstFault = firstName.trim() ? validateName(firstName, 'first name') : null;
  const lastFault = lastName.trim() ? validateName(lastName, 'last name') : null;
  const canSubmit = !saving && !validateName(firstName, 'first name') && !validateName(lastName, 'last name');

  async function submit() {
    if (!canSubmit || !userId) return;
    setSaving(true);
    setError(null);
    try {
      const token = await auth.getAccessToken();
      const result = token ? await updateName(token, { firstName: firstName.trim(), lastName: lastName.trim() }) : 'failed';
      if (result === 'invalid_name') { setError(INVALID_NAME_MESSAGE); return; }
      if (result === 'failed') { setError("We couldn't save your name. Please try again."); return; }
      markNameHandled(userId);
      try {
        await auth.getAccessToken({ forceRefresh: true });
      } catch {
        /* the name is saved; the session user catches up on the next refresh */
      }
      setState('open');
    } catch {
      setError("We couldn't save your name. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  let status: NameGateStatus = 'open';
  if (active && state === 'checking') status = 'waiting';
  else if (active && state === 'prompt') status = 'prompt';
  // Latched during render (not in an effect) so the open page never sees a loader frame later.
  if (stayOpen && status === 'open' && enabled && userId !== null && !shownOpen) setShownOpen(true);

  return {
    status,
    form: {
      firstName,
      lastName,
      setFirstName,
      setLastName,
      firstNameError: firstFault ?? undefined,
      lastNameError: lastFault ?? undefined,
      canSubmit,
      saving,
      error,
      submit,
    },
  };
}
